import React, { useState, useRef, useEffect } from 'react';
import { getFieldValue } from '@sitecore-jss/sitecore-jss-react';
import { Container } from 'components/shared';
import styled from 'styled-components';
import cx from 'classnames';
import { useCountryValue } from 'stores/country';
import useBreakpoint from 'utils/hooks/useBreakpoint';
import { useLocation, useNavigate } from 'react-router-dom';
import queryString from 'query-string';

import loadable from '@loadable/component';
const ContactCorporatePanel = loadable(() =>
  import('components/components/organisms/ContactCorporatePanel'),
);
const ContactCustomerCarePanel = loadable(() =>
  import('components/components/organisms/ContactCustomerCarePanel'),
);
const ContactDesignPanel = loadable(() =>
  import('components/components/organisms/ContactDesignPanel'),
);
const ContactSalesCenterPanel = loadable(() =>
  import('components/components/organisms/ContactSalesCenterPanel'),
);
const ContactMarketCardsPanel = loadable(() =>
  import('components/components/organisms/ContactMarketCardsPanel'),
);

const StyledHeading = styled.h2`
  letter-spacing: -0.01em;
`;
const Copy = styled.div`
  letter-spacing: 0.14px;
`;

const StyledButton = styled.button`
  border-radius: 0.625rem;
`;

const ButtonTab = ({
  title,
  onClick = () => {},
  isSelected = false,
  hasNext = true,
  isExpanded = true,
}) => {
  return (
    <StyledButton
      className={cx(
        'uppercase  font-trade-gothic-20 py-4 px-8 tracking-wide w-full md:w-auto h-60-mattamy text-white cursor-pointer',
        {
          'mb-4 md:mr-4 md:mb-0': hasNext,
          'bg-mattamy-blue hover:bg-contact-blue-color': !isSelected,
          'bg-contact-blue-color': isSelected,
          underline: isSelected,
        },
      )}
      onClick={onClick}
      aria-label={title}
      aria-expanded={isSelected ? true : false}
      tabIndex={isExpanded ? 0 : -1}
    >
      {title}
    </StyledButton>
  );
};

const getFields = (rendering, name) => {
  try {
    const { placeholders } = rendering;
    const componentFields = placeholders[name][0];
    return componentFields;
  } catch (e) {
    return null;
  }
};
const getCustomerCare = (selectedMarket, contactInformationPanel) => {
  try {
    const customerCare = getFieldValue(contactInformationPanel, 'customerCare');
    return (
      customerCare && customerCare.filter((s) => s.market === selectedMarket)
    );
  } catch (e) {
    return null;
  }
};
const getSalesCenter = (selectedMarket, contactInformationPanel) => {
  try {
    const secondSalesCenters = getFieldValue(
      contactInformationPanel,
      'secondSalesCenters',
    );
    const secondSalesCenterFromSelectedMarket =
      secondSalesCenters &&
      secondSalesCenters.filter((s) => s.market === selectedMarket);
    const salesCenters = getFieldValue(contactInformationPanel, 'salesCenters');
    const salesCenterFromSelectedMarket =
      salesCenters && salesCenters.filter((s) => s.market === selectedMarket);

    for (let i = 0; i < salesCenterFromSelectedMarket.length; i++) {
      if (secondSalesCenterFromSelectedMarket.length > 0) {
        const ssc = secondSalesCenterFromSelectedMarket.find(
          (e) =>
            e.communityName === salesCenterFromSelectedMarket[i].communityName,
        );
        if (ssc) salesCenterFromSelectedMarket[i].secondSalesCenters = ssc;
      } else {
        break;
      }
    }

    let getMPCs =
      salesCenterFromSelectedMarket &&
      salesCenterFromSelectedMarket.filter((s) => s.type === 'MPC');

    for (let i = 0; i < salesCenterFromSelectedMarket.length; i++) {
      const ss = salesCenterFromSelectedMarket[i];
      if (ss.mpcid) {
        const MPCIndex = getMPCs.findIndex((sc) => sc.id === ss.mpcid);
        if (MPCIndex === -1) {
          const fakeMPC = {
            id: ss.mpcid,
            Communities: [ss],
            communityName: ss.communityName,
            type: 'MPC',
            salesCenterDescription: ss.salesCenterDescription,
          };

          getMPCs.push(fakeMPC);
        } else {
          if (getMPCs[MPCIndex].Communities) {
            const hasCommunity = getMPCs[MPCIndex].Communities.find(
              (sc) => sc.id === ss.id,
            );
            if (!hasCommunity) getMPCs[MPCIndex].Communities.push(ss);
          } else {
            getMPCs[MPCIndex].Communities = [];
            getMPCs[MPCIndex].Communities.push(ss);
          }
        }
      } else {
        if (ss.type !== 'MPC') getMPCs.push(ss);
      }
    }
    return (
      getMPCs &&
      getMPCs.sort((a, b) =>
        a.communityName.toLowerCase() > b.communityName.toLowerCase() ? 1 : -1,
      )
    );
  } catch (e) {
    return null;
  }
};
const getDesignStudio = (selectedMarket, contactInformationPanel) => {
  try {
    const designStudio = getFieldValue(contactInformationPanel, 'designStudio');
    return (
      designStudio && designStudio.filter((s) => s.market === selectedMarket)
    );
  } catch (e) {
    return null;
  }
};
const getStateAbreviation = (selectedMarket, marketCards) => {
  if (selectedMarket && marketCards && marketCards.length > 0) {
    const market = marketCards.find((m) => m.name === selectedMarket);
    const stateLabel = market && market.state;
    const stateAbreviation = stateLabel && stateLabel.split(',');
    return stateAbreviation && stateAbreviation[1];
  }
  return '';
};
const ContactUsPanel = ({ rendering }) => {
  const marketCards = getFields(rendering, 'market-cards');
  const contactInformationPanel = getFields(rendering, 'contact-panel');
  const corporateInformation = getFields(rendering, 'contact-cards');
  const tabsInformation = getFields(rendering, 'contact-us');

  const location = useLocation();
  const params = queryString.parse(location.search);
  const navigate = useNavigate();
  const [{ country }] = useCountryValue();

  const countryParam = params.country ? params.country : undefined;
  const marketParam = params.market ? params.market : undefined;
  const tabSelectedParam = params.tab ? params.tab : undefined;
  // console.log({ countryParam, marketParam, tabSelectedParam });
  const panelTitle = getFieldValue(tabsInformation, 'title');
  const panelDescription = getFieldValue(tabsInformation, 'description');
  const panelCTA1 = getFieldValue(tabsInformation, 'cTAText1');
  const panelCTA2 = getFieldValue(tabsInformation, 'cTAText2');
  const panelCTA3 = getFieldValue(tabsInformation, 'cTAText3');
  const marketCardsList = getFieldValue(marketCards, 'marketCards');

  const salesCenterTitle =
    country === 'USA'
      ? contactInformationPanel &&
        getFieldValue(contactInformationPanel, 'salesTitle')
      : contactInformationPanel &&
        getFieldValue(contactInformationPanel, 'salesTitleCAN');
  const designTitle =
    contactInformationPanel &&
    getFieldValue(contactInformationPanel, 'designTitle');
  const customerCareTitle =
    contactInformationPanel &&
    getFieldValue(contactInformationPanel, 'customerCareTitle');

  const [hasCustomeCare, setHasCustomeCare] = useState(false);
  const [hasSalesCenter, setHasSalesCenter] = useState(false);
  const [hasDesingCenter, setHasDesingCenter] = useState(false);

  const [isSelectedCustomeCare, setIsSelectedCustomeCare] = useState(false);
  const [isSelectedSalesCenter, setIsSelectedSalesCenter] = useState(false);
  const [isSelectedDesingCenter, setIsSelectedDesingCenter] = useState(false);
  const [ignorNextButtonIndex, setIgnorNextButton] = useState(true);

  const [marketSelected, setMarketSelected] = useState();
  const [panelTabSelected, setPanelTabSelected] = useState();
  const [stateAbreviation, setStateAbreviation] = useState();
  const [customeCareInfo, setCustomeCareInfo] = useState();
  const [salesCenterInfo, setSalesCenterInfo] = useState();
  const [salesCenterCounter, setSalesCenterCounter] = useState();
  const [desingCenterInfo, setDesingCenterInfo] = useState();

  const contentRef = useRef();
  const buttonContentRef = useRef();
  const breakpoint = useBreakpoint(
    typeof window !== 'undefined' && window.innerWidth,
  );
  const isMobile = ['sm', 'xs'].includes(breakpoint);
  const bl = useRef(null);

  const handleMarketCardClick = (
    selectedMarket,
    expand,
    tabSelectedParam = '',
  ) => {
    if (selectedMarket && expand) {
      setMarketSelected(selectedMarket);
      setIsSelectedCustomeCare(false);
      setIsSelectedSalesCenter(false);
      setIsSelectedDesingCenter(false);
      setStateAbreviation(getStateAbreviation(selectedMarket, marketCardsList));
      const customerCare = getCustomerCare(
        selectedMarket,
        contactInformationPanel,
      );
      const salesCenters = getSalesCenter(
        selectedMarket,
        contactInformationPanel,
      );
      const designStudio = getDesignStudio(
        selectedMarket,
        contactInformationPanel,
      );
      const salesCentersCount = getFieldValue(
        contactInformationPanel,
        'salesCenters',
      ).filter((s) => s.type !== 'MPC' && s.market === selectedMarket).length;
      if (customerCare && customerCare.length > 0) {
        setHasCustomeCare(true);
        setCustomeCareInfo(customerCare);
      } else {
        setHasCustomeCare(false);
        setCustomeCareInfo();
      }
      if (salesCenters && salesCenters.length > 0) {
        setHasSalesCenter(true);
        setSalesCenterInfo(salesCenters);
        setSalesCenterCounter(salesCentersCount);
      } else {
        setHasSalesCenter(false);
        setSalesCenterInfo();
      }
      if (designStudio && designStudio.length > 0) {
        setHasDesingCenter(true);
        setDesingCenterInfo(designStudio);
      } else {
        setHasDesingCenter(false);
        setDesingCenterInfo();
      }
      if (!tabSelectedParam) {
        setPanelTabSelected();
      } else {
        setTimeout(() => {
          // console.log({ tabSelectedParam, panelCTA1, panelCTA2, panelCTA3 });
          switch (tabSelectedParam) {
            case panelCTA1:
              handleOnClickPanelCTA1();
              setIsSelectedCustomeCare(true);
              setPanelTabSelected(panelCTA1);
              break;
            case panelCTA2:
              handleOnClickPanelCTA2();
              setIsSelectedSalesCenter(true);
              setPanelTabSelected(panelCTA2);
              break;
            case panelCTA3:
              handleOnClickPanelCTA3();
              setIsSelectedDesingCenter(true);
              setPanelTabSelected(panelCTA3);
              break;
            default:
              setPanelTabSelected(tabSelectedParam);
              break;
          }
        }, 1000);
      }
    } else {
      setMarketSelected();
      setHasCustomeCare(false);
      setHasSalesCenter(false);
      setHasDesingCenter(false);
      setSalesCenterCounter();
      setDesingCenterInfo();
      setCustomeCareInfo();
      setSalesCenterInfo();
      setPanelTabSelected();
    }
  };
  const handleOnClickPanelCTA1 = () => {
    if (isSelectedCustomeCare) {
      setPanelTabSelected();
    } else {
      setPanelTabSelected(panelCTA1);
    }
    setIsSelectedCustomeCare(!isSelectedCustomeCare);
    setIsSelectedSalesCenter(false);
    setIsSelectedDesingCenter(false);
    setIgnorNextButton(false);
  };
  const handleOnClickPanelCTA2 = () => {
    if (isSelectedSalesCenter) {
      setPanelTabSelected();
    } else {
      setPanelTabSelected(panelCTA2);
    }
    setIsSelectedCustomeCare(false);
    setIsSelectedSalesCenter(!isSelectedSalesCenter);
    setIsSelectedDesingCenter(false);
    setIgnorNextButton(false);
  };
  const handleOnClickPanelCTA3 = () => {
    if (isSelectedDesingCenter) {
      setPanelTabSelected();
    } else {
      setPanelTabSelected(panelCTA3);
    }
    setIsSelectedCustomeCare(false);
    setIsSelectedSalesCenter(false);
    setIsSelectedDesingCenter(!isSelectedDesingCenter);
    setIgnorNextButton(false);
  };

  useEffect(() => {
    // console.log("cambio de country");
    handleMarketCardClick();
  }, [country]);

  useEffect(() => {
    if (!marketSelected && marketParam) {
      /* console.log(
        "handleMarketCardClick useEffect",
        marketParam,
        tabSelectedParam,
        marketCardsList
      );*/
      const marketExist = marketCardsList.find(
        (e) => e.name.toLowerCase() === marketParam.toLowerCase(),
      );
      // console.log("cambio de marketParam", tabSelectedParam);
      if (marketExist && marketExist.name) {
        handleMarketCardClick(marketExist.name, true, tabSelectedParam);
      }
    }
  }, [marketParam, marketCardsList]);

  useEffect(() => {
    // console.log("cambio de tabSelectedParam", {panelTabSelected, tabSelectedParam, panelCTA1, panelCTA2, panelCTA3});
    if (!panelTabSelected && tabSelectedParam) {
      /* console.log(
        "handleMarketCardClick useEffect",
        marketParam,
        tabSelectedParam,
        marketCardsList
      );*/
      const marketExist = marketCardsList.find(
        (e) => e.name.toLowerCase() === marketParam.toLowerCase(),
      );
      // console.log("cambio de tabSelectedParam", tabSelectedParam);
      if (marketExist && marketExist.name) {
        handleMarketCardClick(marketExist.name, true, tabSelectedParam);
      }
    }
  }, [tabSelectedParam, panelCTA1, panelCTA2, panelCTA3]);

  useEffect(() => {
    if (marketSelected && contentRef && contentRef.current) {
      setTimeout(() => {
        const topScroll = contentRef.current.offsetTop || 0;
        //console.log('here!', marketSelected, topScroll);
        window.scrollTo({
          top: topScroll,
          left: 0,
          behavior: 'smooth',
        });
        contentRef.current.focus();
      }, 1000);
    }
  }, [marketSelected]);

  useEffect(() => {
    const newParams = {
      ...params,
      country,
    };
    // console.log({ marketSelected, panelTabSelected });
    if (marketSelected) newParams.market = marketSelected;
    else delete newParams.market;
    if (panelTabSelected) newParams.tab = panelTabSelected;
    else delete newParams.tab;
    // console.log({ newParams });
    navigate({
      pathname: location.pathname,
      search: '?' + new URLSearchParams(newParams).toString(),
    });
  }, [marketSelected, panelTabSelected]);

  /* useEffect(() => {
    if (marketSelected && buttonContentRef && buttonContentRef.current) {
      setTimeout(() => {
        const topScroll = buttonContentRef.current.offsetTop || 0;
        window.scrollTo({
          top: topScroll - 100,
          left: 0,
          behavior: 'smooth',
        });
      }, 2000);
    }
  }, [isSelectedCustomeCare, isSelectedSalesCenter, isSelectedDesingCenter]);*/

  if (!contactInformationPanel || !marketCards) return null;
  return (
    <>
      <Container>
        <StyledHeading
          aria-label='Country Selector'
          className='text-l md:text-2xl text-mattamy-blue font-trade-gothic-20  mt-2'
        >
          {'Country Selector'}
        </StyledHeading>
      </Container>

      <ContactMarketCardsPanel
        fields={marketCards}
        contactPanelFields={contactInformationPanel}
        handleMarketCardClick={handleMarketCardClick}
        marketParam={marketParam}
        countryParam={countryParam}
        tabSelectedParam={tabSelectedParam}
        selectedMarket={marketSelected}
      />
      {(hasCustomeCare || hasSalesCenter || hasDesingCenter) && (
        <Container className='my-8 md:mt-8 md:mb-16'>
          <StyledHeading className='text-3xl md:text-4xl text-mattamy-blue font-trade-gothic-20  mb-3 md:mb-2'>
            {marketSelected + ', ' + stateAbreviation}
          </StyledHeading>
          <StyledHeading className='text-3xl md:text-4xl text-mattamy-blue font-trade-gothic-20  mb-3 md:mb-2'>
            {panelTitle}
          </StyledHeading>

          <Copy
            className={
              'mt-0 text-lg md:mt-2 md:text-xl leading-7 font-graphie text-mattamy-blue text-left'
            }
          >
            {panelDescription}
          </Copy>

          <div
            ref={contentRef}
            tabIndex={0}
            onFocus={(e) => {
              if (!contentRef.current.contains(e.relatedTarget)) {
                Array.from(contentRef.current.children).forEach((child) => {
                  child.tabIndex = 0;
                });
                setIgnorNextButton(true);
                contentRef.current.firstChild.focus();
              }
            }}
            className={'my-8 flex flex-col md:flex-row'}
          >
            {hasCustomeCare && panelCTA1 && (
              <ButtonTab
                title={panelCTA1}
                onClick={handleOnClickPanelCTA1}
                isSelected={isSelectedCustomeCare}
                hasNext={hasSalesCenter || hasDesingCenter}
                isExpanded={ignorNextButtonIndex}
              />
            )}
            {hasSalesCenter && panelCTA2 && (
              <ButtonTab
                title={panelCTA2}
                onClick={handleOnClickPanelCTA2}
                isSelected={isSelectedSalesCenter}
                hasNext={hasDesingCenter}
                isExpanded={ignorNextButtonIndex}
              />
            )}
            {hasDesingCenter && panelCTA3 && (
              <ButtonTab
                title={panelCTA3}
                onClick={handleOnClickPanelCTA3}
                isSelected={isSelectedDesingCenter}
                hasNext={false}
                isExpanded={ignorNextButtonIndex}
              />
            )}
          </div>

          {(isSelectedCustomeCare ||
            isSelectedDesingCenter ||
            isSelectedSalesCenter) && (
            <div className='border-b-2 border-light-blue w-full mt-4 mb-8 md:my-12'></div>
          )}
        </Container>
      )}
      <div ref={buttonContentRef}>
        {isSelectedCustomeCare && (
          <ContactCustomerCarePanel
            stateAbreviation={stateAbreviation}
            customerCareTitle={customerCareTitle}
            marketSelected={marketSelected}
            customeCareInfo={customeCareInfo}
          />
        )}
        {isSelectedSalesCenter && (
          <ContactSalesCenterPanel
            stateAbreviation={stateAbreviation}
            title={salesCenterTitle}
            marketSelected={marketSelected}
            salesCenter={salesCenterInfo}
            salesCenterCounter={salesCenterCounter}
          />
        )}
        {isSelectedDesingCenter && (
          <ContactDesignPanel
            title={designTitle}
            desingCenter={desingCenterInfo}
            stateAbreviation={stateAbreviation}
            marketSelected={marketSelected}
          />
        )}
        {corporateInformation && (
          <ContactCorporatePanel
            title={' Contact Our Corporate Offices '}
            fields={corporateInformation}
          />
        )}
      </div>
    </>
  );
};
export default ContactUsPanel;
